import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  FolderOpen,
  Search,
  Loader2,
  FileText,
  Image,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KPI } from '@/components/ui/kpi';
import { Pill } from '@/components/ui/pill';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import {
  useDocumentos,
  useDocumentosAggregate,
  useDeleteDocumento,
  type Documento,
} from '@/lib/hooks/documentos';
import { DocumentoDrawer } from '@/features/documentos/DocumentoDrawer';
import { api } from '@/lib/api';
import { fmtDate, fmtPlural } from '@/lib/utils';

export const Route = createFileRoute('/_app/documentos')({
  component: DocumentosPage,
});

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageMime(mime: string) {
  return mime.startsWith('image/');
}

async function openPresignedUrl(r2Key: string) {
  try {
    const { url } = await api.post<{ url: string }>('/v1/uploads/view', { key: r2Key });
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    toast.error(err instanceof Error ? err.message : 'Falha ao gerar link de visualização.');
  }
}

// Extract unique tags from the current page items
function extractUniqueTags(items: Documento[]): string[] {
  const set = new Set<string>();
  for (const doc of items) {
    for (const tag of doc.tags) set.add(tag);
  }
  return Array.from(set).sort();
}

function DocumentosPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<string>('ALL');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Documento | null>(null);

  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      tag: tagFilter === 'ALL' ? undefined : tagFilter,
      limit: 60,
      offset: 0,
    }),
    [search, tagFilter],
  );

  const listQ = useDocumentos(workspaceId, filters);
  const aggQ = useDocumentosAggregate(workspaceId);
  const delMut = useDeleteDocumento(workspaceId);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const uniqueTags = useMemo(() => extractUniqueTags(items), [items]);

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await delMut.mutateAsync(confirmDelete.id);
      toast.success('Documento removido.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para acessar a biblioteca.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Workspace</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Biblioteca de documentos
          </h1>
          <p className="text-sm text-fg-muted mt-2">
            Armazene PDFs, imagens e comprovantes avulsos do workspace.
          </p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Novo documento
        </Button>
      </header>

      {/* KPI */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI
          tone="mint"
          label="Total de documentos"
          value={String(aggQ.data?.count ?? 0)}
          delta={fmtPlural(aggQ.data?.count ?? 0, 'arquivo', 'arquivos')}
          icon={FolderOpen}
        />
        <KPI
          label="PDFs"
          value={String(aggQ.data?.byType?.application ?? 0)}
          delta="application/*"
          icon={FileText}
        />
        <KPI
          tone="ink"
          label="Imagens"
          value={String(aggQ.data?.byType?.image ?? 0)}
          delta="image/*"
          icon={Image}
        />
      </section>

      {/* Filtros */}
      <Card variant="default" className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted"
              strokeWidth={1.8}
            />
            <Input
              placeholder="Buscar por título…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={tagFilter}
            onValueChange={(v) => setTagFilter(v)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todas as tags" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas as tags</SelectItem>
              {uniqueTags.map((tag) => (
                <SelectItem key={tag} value={tag}>
                  {tag}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Grid de cards */}
      {listQ.isLoading ? (
        <div className="flex items-center justify-center py-16 text-fg-muted">
          <Loader2 className="h-5 w-5 animate-spin mr-2" strokeWidth={1.8} />
          Carregando…
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-3 border border-dashed border-card-stroke bg-card-soft/60 py-16 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-mint-soft text-mint-deep">
            <FolderOpen className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-sm font-semibold text-fg">Nenhum documento encontrado</p>
            <p className="text-[13px] text-fg-muted mt-0.5">
              {search || tagFilter !== 'ALL'
                ? 'Tente remover os filtros.'
                : 'Clique em "Novo documento" para começar.'}
            </p>
          </div>
          {!search && tagFilter === 'ALL' && (
            <Button variant="primary" size="sm" onClick={() => setDrawerOpen(true)}>
              <Plus className="h-3.5 w-3.5" strokeWidth={1.8} />
              Adicionar primeiro documento
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {items.map((doc) => (
              <DocumentoCard
                key={doc.id}
                doc={doc}
                onDelete={() => setConfirmDelete(doc)}
              />
            ))}
          </div>
          {total > items.length && (
            <p className="text-center text-[13px] text-fg-muted">
              Mostrando {items.length} de {total} documentos. Use a busca para refinar.
            </p>
          )}
        </>
      )}

      <DocumentoDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir este documento?"
        description={
          confirmDelete
            ? `"${confirmDelete.titulo}" será removido da biblioteca. Não dá pra desfazer.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={delMut.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}

interface DocumentoCardProps {
  doc: Documento;
  onDelete: () => void;
}

function DocumentoCard({ doc, onDelete }: DocumentoCardProps) {
  const [viewLoading, setViewLoading] = useState(false);

  async function handleView() {
    setViewLoading(true);
    try {
      await openPresignedUrl(doc.r2Key);
    } finally {
      setViewLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-3 border border-card-stroke bg-card p-4 shadow-1 hover:shadow-clay transition-shadow">
      {/* Icon + título */}
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2 bg-mint-soft text-mint-deep">
          {isImageMime(doc.mimeType) ? (
            <Image className="h-5 w-5" strokeWidth={1.5} />
          ) : (
            <FileText className="h-5 w-5" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-fg leading-tight line-clamp-2">{doc.titulo}</p>
          <p className="text-[11px] text-fg-muted font-mono mt-0.5">
            {fmtBytes(doc.tamanhoBytes)} · {fmtDate(doc.createdAt)}
          </p>
        </div>
      </div>

      {/* Descrição */}
      {doc.descricao && (
        <p className="text-[13px] text-fg-muted leading-snug line-clamp-2">{doc.descricao}</p>
      )}

      {/* Tags */}
      {doc.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {doc.tags.map((tag) => (
            <Pill key={tag} tone="mint">{tag}</Pill>
          ))}
        </div>
      )}

      {/* Ações */}
      <div className="flex items-center gap-2 pt-1 border-t border-card-stroke/60 mt-auto">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="flex-1"
          onClick={handleView}
          disabled={viewLoading}
        >
          {viewLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.8} />
          ) : (
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.8} />
          )}
          Ver
        </Button>
        <button
          type="button"
          onClick={onDelete}
          aria-label="Excluir documento"
          className="grid h-8 w-8 place-items-center rounded-1 text-fg-muted hover:text-warn hover:bg-warn-soft transition-colors"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}
